package top.wcpe.allincore.ui.runtime.animation

import top.wcpe.allincore.dsl.ir.model.animation.SpringSpec
import kotlin.jvm.JvmField
import kotlin.math.abs

/**
 * 一维弹簧物理求解器（纯函数，无副作用）。
 *
 * 用半隐式欧拉（symplectic Euler）按 dt 积分单个标量轴的弹簧运动，状态（position / velocity /
 * target）由调用方持有，本类不保存任何可变状态。受力模型：
 * `F = -stiffness * (position - target) - damping * velocity`，加速度 `a = F / mass`。
 *
 * 子步进：dt 过大（掉帧）时显式积分会发散，[step] 内部按固定子步长 [SUBSTEP_SEC] 拆分 dt
 * 循环积分，保证大 dt 下仍稳定收敛。
 */
class Spring(spec: SpringSpec) {

    private val stiffness: Double = spec.stiffness
    private val damping: Double = spec.damping
    private val mass: Double = spec.mass

    /** 单步积分结果：新的位置与速度（保留公开 final 字段 ABI，调用方以 step.position 访问）。 */
    class SpringStep(@JvmField val position: Double, @JvmField val velocity: Double)

    /**
     * 按 dt 推进一步弹簧运动，返回新的位置与速度。
     *
     * dt 过大时内部按固定子步长拆分循环积分（半隐式欧拉），抑制发散。dt 非正时原样返回当前状态。
     *
     * @param position 当前位置
     * @param velocity 当前速度
     * @param target   目标位置
     * @param dtSec    本帧时间增量（秒）
     * @return 积分后的新位置与速度
     */
    fun step(position: Double, velocity: Double, target: Double, dtSec: Double): SpringStep {
        if (dtSec <= 0.0) {
            return SpringStep(position, velocity)
        }
        var remaining = dtSec
        var pos = position
        var vel = velocity
        while (remaining > 0.0) {
            val h = minOf(SUBSTEP_SEC, remaining)
            // 半隐式欧拉：先用当前位置算力更新速度，再用新速度更新位置。
            val force = -stiffness * (pos - target) - damping * vel
            val acceleration = force / mass
            vel += acceleration * h
            pos += vel * h
            remaining -= h
        }
        return SpringStep(pos, vel)
    }

    /**
     * rest 判定：位置距目标足够近且速度足够小，视为弹簧静止可结束。
     *
     * @param position 当前位置
     * @param velocity 当前速度
     * @param target   目标位置
     * @return 进入静止时返回 true
     */
    fun atRest(position: Double, velocity: Double, target: Double): Boolean =
        abs(position - target) < REST_POSITION_EPSILON && abs(velocity) < REST_VELOCITY_EPSILON

    companion object {
        /** 子步长（秒）：dt 大于此值时拆分多次积分，1/240s 在常见弹簧参数下足够稳定。 */
        private const val SUBSTEP_SEC = 1.0 / 240.0

        /** 位置静止容差：位置距目标小于此值视为到位。 */
        private const val REST_POSITION_EPSILON = 0.01

        /** 速度静止容差：速度绝对值小于此值视为静止。 */
        private const val REST_VELOCITY_EPSILON = 0.01
    }
}
